import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    Grid,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Menu,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Fade,
    Chip,
    Divider,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as ContentCopyIcon,
    Link as LinkIcon,
    Code as CodeIcon,
    BarChart as BarChartIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Download as DownloadIcon,
    ArrowDropDown as ArrowDropDownIcon,
    ArrowBack as ArrowBackIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { colors, routes } from "../../constants";
import { darkenColor } from "../../utils/colorUtils";
import SimpleDialog from "../../components/SimpleDialog";
import ResponsesModal from "../../components/ResponsesModal";
import CollectorCard from "../../components/Collector/CollectorCard";
import api from "../../api";

// Helper function to get collector status styling
const getCollectorStatusStyling = (status) => {
    switch (status?.toLowerCase()) {
        case 'active':
            return {
                color: '#2e7d32',
                bgColor: 'rgba(76, 175, 80, 0.2)',
                borderColor: '#4caf50',
                shadowColor: 'rgba(76, 175, 80, 0.3)',
                iconColor: '#4caf50'
            };
        case 'draft':
            return {
                color: '#616161',
                bgColor: 'rgba(158, 158, 158, 0.15)',
                borderColor: '#9e9e9e',
                shadowColor: 'rgba(158, 158, 158, 0.2)',
                iconColor: '#757575'
            };
        case 'completed':
            return {
                color: '#d32f2f',
                bgColor: 'rgba(244, 67, 54, 0.2)',
                borderColor: '#f44336',
                shadowColor: 'rgba(244, 67, 54, 0.3)',
                iconColor: '#f44336'
            };
        default:
            return {
                color: '#616161',
                bgColor: 'rgba(158, 158, 158, 0.15)',
                borderColor: '#9e9e9e',
                shadowColor: 'rgba(158, 158, 158, 0.2)',
                iconColor: '#757575'
            };
    }
};

function CollectList() {
    const navigate = useNavigate();
    const { surveyId } = useParams();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { formatMessage } = useIntl();

    const [collectors, setCollectors] = useState([]);
    const [dialog, setDialog] = useState({ open: false, id: null, name: "" });
    const [lockDialog, setLockDialog] = useState({ open: false, id: null, title: "", content: "", is_locked: false, recipientsCount: 0 });
    const [createDialog, setCreateDialog] = useState({ open: false });
    const [showFailedEmails, setShowFailedEmails] = useState(false);
    const [editingEmail, setEditingEmail] = useState({ id: null, email: "", reason: "", email_recipient_id: "" });
    const [newCollector, setNewCollector] = useState({
        name: "",
        type: "",
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasCollector, setHasCollector] = useState(false);
    const [surveyStatus, setSurveyStatus] = useState(location.state?.surveyStatus || null);
    const [surveyTitle, setSurveyTitle] = useState(location.state?.surveyTitle || null);

    // useless 
    useEffect(() => {
        document.body.style.backgroundColor = "#f5f5f5";
        return () => {
            document.body.style.backgroundColor = "";
        };
    }, []);

    // fetch survey status, if surveyId is available, but status is not
    useEffect(() => {
        let alive = true;
        (async () => {
            if (!surveyStatus && surveyId) {
                try {
                    const survey = await api.getSurvey(surveyId);
                    if (alive) setSurveyStatus(survey.status || null);
                } catch (e) {
                    // ignore; UI will behave as if status unknown
                }
            }
        })();
        return () => { alive = false; };
    }, [surveyId]);

    // fetch collectors for the surveyId we are in
    const getCollector = async () => {
        setLoading(true);
        setError(null);
        try {
            const collectorsData = await api.getCollector(surveyId);
            const validCollectorsData = Array.isArray(collectorsData) ? collectorsData : [];
            setCollectors(validCollectorsData);
            setHasCollector(validCollectorsData.length > 0);
            console.log("Initial collectorsData:", validCollectorsData);
        } catch (err) {
            console.error("Error fetching collectors:", err);
            setError(err.message || "Failed to fetch collectors");
            setCollectors([]);
            setHasCollector(false);
            enqueueSnackbar(<FormattedMessage id="collect_list.fetch_error" />, {
                variant: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    // if surveyId changes (somehow), fetch collectors again
    useEffect(() => {
        getCollector();
        (async () => {
            try {
                const survey = await api.getSurvey(surveyId);
                setSurveyTitle('"' + survey.title + '"');
            } catch (e) {
                setSurveyTitle('Assessment #' + surveyId);
                console.error("Error fetching survey title:", e);
            }
        })();
    }, [surveyId]);

    const fetchFailedEmails = async (collectorId) => {
        console.log("fetchFailedEmails called");
        try {
            const failedEmailsData = await api.getFailedEmails(surveyId, collectorId);
            setCollectors((prevCollectors) =>
                prevCollectors.map((collector) =>
                    collector.id === collectorId
                        ? { ...collector, failedEmails: failedEmailsData || [] }
                        : collector
                )
            );
        } catch (err) {
            console.error("Error fetching failed emails:", err);
            enqueueSnackbar(<FormattedMessage id="collect_list.fetch_failed_emails_error" />, { variant: "error" });
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await api.deleteCollector(surveyId, dialog.id);
            await getCollector();
            setHasCollector(false);
            enqueueSnackbar(<FormattedMessage id="collect_list.delete_success" />, {
                variant: "success",
                autoHideDuration: 2000,
                anchorOrigin: { vertical: "bottom", horizontal: "right" },
            });
        } catch (err) {
            console.error(`Error deleting collector ${dialog.id}:`, err);
            enqueueSnackbar(<FormattedMessage id="collect_list.delete_error" />, { variant: "error" });
        } finally {
            setDialog({ open: false, id: null, name: "" });
            setLoading(false);
        }
    };

    const handleCopyLink = (link) => {
        const fullUrl = link.startsWith('http') ? link : `${window.location.origin}${link}`;
        navigator.clipboard.writeText(fullUrl);
        enqueueSnackbar(<FormattedMessage id="collect_list.copy_link_success" />, { variant: "success" });
    };

    const handleDuplicateCollector = async (collector) => {
        try {
            const result = await api.duplicateCollector(surveyId, collector.id);
            await getCollector();
            enqueueSnackbar(
                formatMessage({ id: "collect_list.duplicate_success", defaultMessage: "Collector duplicated successfully." }),
                { variant: "success", autoHideDuration: 2000, anchorOrigin: { vertical: "bottom", horizontal: "right" } }
            );
        } catch (err) {
            console.error(`Error duplicating collector ${collector.id}:`, err);
            enqueueSnackbar(
                formatMessage({ id: "collect_list.duplicate_error", defaultMessage: "Failed to duplicate collector." }),
                { variant: "error" }
            );
        }
    };

    const handleSendReminder = async (collectorId, options) => {
        try {
            await api.sendReminderEmail(collectorId, options);
            const isSchedule = options?.scheduledTime != null && options.scheduledTime !== "";
            enqueueSnackbar(
                formatMessage(
                    isSchedule
                        ? {
                              id: "collect_list.reminder_scheduled_success",
                              defaultMessage: "Reminder scheduled successfully.",
                          }
                        : {
                              id: "collect_list.reminder_success",
                              defaultMessage: "Reminder sent successfully.",
                          }
                ),
                { variant: "success", autoHideDuration: 2000, anchorOrigin: { vertical: "bottom", horizontal: "right" } }
            );
        } catch (err) {
            console.error(`Error sending reminder for collector ${collectorId}:`, err);
            enqueueSnackbar(
                formatMessage({ id: "collect_list.reminder_error", defaultMessage: "Failed to send reminder." }),
                { variant: "error" }
            );
            throw err;
        }
    };

    const handleRenameCollector = async (collectorId, newName) => {
        try {
            await api.renameCollector(surveyId, collectorId, newName);
            setCollectors((prev) =>
                prev.map((c) => (c.id === collectorId ? { ...c, name: newName } : c))
            );
            enqueueSnackbar(
                formatMessage({ id: "collect_list.rename_success", defaultMessage: "Collector renamed successfully." }),
                { variant: "success", autoHideDuration: 2000, anchorOrigin: { vertical: "bottom", horizontal: "right" } }
            );
        } catch (err) {
            console.error(`Error renaming collector ${collectorId}:`, err);
            enqueueSnackbar(
                formatMessage({ id: "collect_list.rename_error", defaultMessage: "Failed to rename collector." }),
                { variant: "error" }
            );
            throw err; // re-throw so the card keeps the input open
        }
    };

    const handleToggleLock = async (collectorId) => {
        try {
            console.log("Toggling lock for collector:", collectorId);
            console.log("Survey ID:", surveyId);

            const collector = collectors.find((c) => c.id === collectorId);
            if (!collector) {
                console.error("Collector not found:", collectorId);
                enqueueSnackbar("Collector not found", { variant: "error" });
                return;
            }

            const updatedCollector = { ...collector, is_locked: !collector.is_locked };
            console.log("Updated collector data:", updatedCollector);

            await api.updateCollector(surveyId, collectorId, updatedCollector);
            await getCollector();
            enqueueSnackbar(
                updatedCollector.is_locked ? (
                    <FormattedMessage id="collect_list.locked" />
                ) : (
                    <FormattedMessage id="collect_list.unlocked" />
                ),
                { variant: "success" }
            );
        } catch (err) {
            console.error("Error toggling lock:", err);
            console.error("Error details:", err.response?.data || err.message);
            enqueueSnackbar(<FormattedMessage id="collect_list.update_error" />, { variant: "error" });
        }
    };

    const openConfirmCollectorLock = (collector) => {
        const isLocked = !!collector.is_locked;
        const recipientsCount = collector.totalInvitations || (collector.email_recipients ? collector.email_recipients.length : 0) || 0;
        const title = isLocked ? <FormattedMessage id="collect_list.unlock_collector" /> : <FormattedMessage id="collect_list.lock_collector" />;
        const content = isLocked
            ? formatMessage({ id: "collector.unlock_confirm" }, { count: recipientsCount })
            : formatMessage({ id: "collector.lock_confirm" }, { count: recipientsCount });
        setLockDialog({ open: true, id: collector.id, title, content, is_locked: isLocked, recipientsCount });
    };

    const handleLockAll = async () => {
        try {
            const allLocked = collectors.every(c => c.is_locked);
            const action = allLocked ? 'unlock' : 'lock';

            // Show confirmation dialog
            const confirmed = window.confirm(
                allLocked
                    ? formatMessage({ id: "collector.unlock_all_confirm" }, { count: collectors.length })
                    : formatMessage({ id: "collector.lock_all_confirm" }, { count: collectors.length })
            );

            if (!confirmed) return;

            // Process all collectors
            const promises = collectors.map(collector => {
                const updatedCollector = { ...collector, is_locked: !allLocked };
                return api.updateCollector(surveyId, collector.id, updatedCollector);
            });

            await Promise.all(promises);
            await getCollector();

            enqueueSnackbar(
                allLocked
                    ? `All ${collectors.length} collectors have been unlocked`
                    : `All ${collectors.length} collectors have been locked`,
                { variant: "success" }
            );
        } catch (err) {
            console.error("Error toggling lock for all collectors:", err);
            enqueueSnackbar("Failed to update collectors", { variant: "error" });
        }
    };

    const handleCreateCollector = async () => {
        if (!newCollector.name || !newCollector.type) {
            enqueueSnackbar(<FormattedMessage id="collect_list.create_error" />, { variant: "error" });
            return;
        }

        if (surveyStatus === "closed") {
            enqueueSnackbar(<FormattedMessage id="collect_list.one_collector_limit" />, { variant: "warning" });
            return;
        }

        const newCollectorData = {
            survey_id: parseInt(surveyId),
            name: newCollector.name,
            type: newCollector.type,
        };
        try {
            const response = await api.createCollector(newCollectorData);
            console.log("Create Response:", response);
            if (response.success) {
                enqueueSnackbar(
                    <FormattedMessage id="collect_list.create_success" />,
                    {
                        variant: "success",
                        autoHideDuration: 2000,
                        anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    }
                );
                setCreateDialog({ open: false });
                setNewCollector({ name: "", type: "" });
                navigate(`${routes.collector}/${surveyId}/${response.collectorId}/configure`);
                return;
            } else {
                throw new Error(response.error || "Failed to create collector");
            }
        } catch (error) {
            console.error("Error creating collector:", error);
            enqueueSnackbar(<FormattedMessage id="collect_list.create_error" />, { variant: "error" });
        }
        setCreateDialog({ open: false });
        setNewCollector({ name: "", type: "" });
    };

    const handleRetryEmail = async (surveyId, collectorId, emailLogId, email) => {
        try {
            const emailBody = {
                'email': email
            };
            await api.retryEmail(surveyId, collectorId, emailLogId, emailBody);
            setCollectors((prevCollectors) =>
                prevCollectors.map((collector) =>
                    collector.id === collectorId
                        ? {
                            ...collector,
                            failedEmails: collector.failedEmails.filter((e) => e.email !== email),
                            failedEmailCount: collector.failedEmailCount - 1,
                            successEmailCount: collector.successEmailCount + 1,
                        }
                        : collector
                )
            );
            enqueueSnackbar(<FormattedMessage id="collect_list.retry_email_success" />, { variant: "success" });
        } catch (err) {
            console.error("Error retrying email:", err);
            enqueueSnackbar(<FormattedMessage id="collect_list.retry_email_error" />, { variant: "error" });
        }
    };

    const handleRetryAllEmails = async (collectorId) => {
        try {
            await api.retryAllEmails(collectorId);
            setCollectors((prevCollectors) =>
                prevCollectors.map((collector) =>
                    collector.id === collectorId
                        ? {
                            ...collector,
                            failedEmails: [],
                            failedEmailCount: 0,
                            successEmailCount: collector.successEmailCount + collector.failedEmailCount,
                        }
                        : collector
                )
            );
            enqueueSnackbar(<FormattedMessage id="collect_list.retry_all_emails_success" />, { variant: "success" });
            setShowFailedEmails(false);
        } catch (err) {
            console.error("Error retrying all emails:", err);
            enqueueSnackbar(<FormattedMessage id="collect_list.retry_all_emails_error" />, { variant: "error" });
        }
    };

    const handleEditEmail = (id, email, reason, email_recipient_id) => {
        setEditingEmail({ id, email, reason, email_recipient_id });
    };

    const handleSaveEditedEmail = async (collectorId) => {
        if (!editingEmail.email) {
            enqueueSnackbar(<FormattedMessage id="collect_list.edit_email_invalid" />, { variant: "error" });
            return;
        }

        try {
            const emailContent = {
                'email': editingEmail.email,
                'email_recipient_id': editingEmail.email_recipient_id
            }
            console.log(editingEmail)
            const response = await api.updateEmail(surveyId, collectorId, editingEmail.id, emailContent);
            if (response.success) {
                setCollectors((prevCollectors) =>
                    prevCollectors.map((collector) =>
                        collector.id === collectorId
                            ? {
                                ...collector,
                                failedEmails: collector.failedEmails.map((e) =>
                                    e.id === editingEmail.id ? { ...e, id: editingEmail.id, email: editingEmail.email, reason: "" } : e
                                ),
                            }
                            : collector
                    )
                );
                enqueueSnackbar(<FormattedMessage id="collect_list.edit_email_success" />, { variant: "success" });
                setEditingEmail({ id: null, email: "", reason: "", email_recipient_id: "" });
            } else {
                throw new Error(response.error || "Failed to update email");
            }
        } catch (err) {
            console.error("Error editing email:", err);
            enqueueSnackbar(<FormattedMessage id="collect_list.edit_email_error" />, { variant: "error" });
        }
    };

    const handleBackToSurveyList = () => {
        navigate("/surveys");
    };

    return (
        <Box
            sx={{
                maxWidth: "1500px",
                margin: "0 auto",
                padding: "32px",
                minHeight: "calc(100vh - 64px)",
                maxHeight: "calc(100vh - 64px)",
                overflowY: "hidden",
                display: "flex",
                flexDirection: "column",
                pt: 6,
            }}
        >
            {/* header */}
            {surveyStatus === "closed" ? (
                <Box sx={Styles.closedSurveyBanner}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tooltip title={<FormattedMessage id="nav.back_to_survey_list" />}>
                            <IconButton
                                onClick={handleBackToSurveyList}
                                sx={Styles.backButton}
                            >
                                <ArrowBackIcon sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                        </Tooltip>
                        <Box>
                            <Typography variant="h6" sx={{ color: "#f44336", fontWeight: "bold", mb: 0.5 }}>
                                <FormattedMessage id="collect_list.survey_locked_title" values={{ surveyTitle }} />
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#d32f2f" }}>
                                <FormattedMessage id="collect_list.survey_locked_description" />
                            </Typography>
                        </Box>
                    </Box>

                    <ErrorIcon sx={{ color: "#f44336", fontSize: "1.5rem" }} />
                </Box>
            ) : (
                <Box sx={Styles.headerBox}>
                    {/* left */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <Tooltip title={<FormattedMessage id="nav.back_to_survey_list" />}>
                            <IconButton
                                onClick={handleBackToSurveyList}
                                sx={Styles.backButton}
                            >
                                <ArrowBackIcon sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                        </Tooltip>

                        <Box>
                            <Typography
                                sx={[
                                    {
                                        color: colors.main,
                                        background: `linear-gradient(135deg, ${colors.main || '#00438E'}, #4A87C8)`
                                    },
                                    Styles.headerTitle
                                ]}
                            >
                                <FormattedMessage id="collect_list.header" values={{ surveyTitle }} />
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={Styles.headerSubtitle}
                            >
                                {collectors.length} collector{collectors.length !== 1 ? 's' : ''} • {collectors.filter(c => c.is_locked).length} locked
                            </Typography>
                        </Box>
                    </Box>

                    {/* right */}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        {/* Lock All Button */}
                        <Button
                            variant="outlined"
                            disabled={surveyStatus === "closed" || collectors.length === 0}
                            sx={{
                                border: `2px solid ${colors.main || '#00438E'}`,
                                color: colors.main || '#00438E',
                                borderRadius: "16px",
                                textTransform: "none",
                                px: 3,
                                py: 1.5,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                minWidth: "auto",
                                whiteSpace: "nowrap",
                                backgroundColor: 'rgba(0, 67, 142, 0.05)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 67, 142, 0.1)',
                                    borderColor: colors.main || '#00438E',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0, 67, 142, 0.2)'
                                },
                                '&:disabled': {
                                    borderColor: "#cccccc",
                                    color: "#999999",
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                },
                                '& .MuiButton-startIcon': {
                                    marginRight: "8px",
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                            startIcon={<LockIcon sx={{ fontSize: '1rem' }} />}
                            onClick={handleLockAll}
                        >
                            {collectors.filter(c => c.is_locked).length === collectors.length ?
                                <FormattedMessage id="home.unlock_all" /> :
                                <FormattedMessage id="home.lock_all" />
                            }
                        </Button>

                        {/* Create Collector Button */}
                        <Button
                            variant="contained"
                            disabled={surveyStatus === "closed"}
                            sx={{
                                background: `linear-gradient(135deg, ${colors.main || '#00438E'}, #4A87C8)`,
                                color: "#ffffff",
                                borderRadius: "16px",
                                textTransform: "none",
                                px: 3,
                                py: 1,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                minWidth: "auto",
                                whiteSpace: "nowrap",
                                boxShadow: "0 4px 16px rgba(0, 67, 142, 0.3)",
                                "&:hover": {
                                    background: `linear-gradient(135deg, ${colors.main ? darkenColor(colors.main, 0.1) : '#003A75'}, #3A76B8)`,
                                    transform: 'translateY(-2px)',
                                    boxShadow: "0 6px 20px rgba(0, 67, 142, 0.4)",
                                },
                                "&:disabled": {
                                    background: "#cccccc",
                                    color: "#666666",
                                    boxShadow: "none",
                                    transform: 'none'
                                },
                                "& .MuiButton-startIcon": {
                                    color: "#ffffff",
                                    marginRight: "8px",
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                            startIcon={<span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>+</span>}
                            onClick={() => setCreateDialog({ open: true })}
                        >
                            <FormattedMessage id="collect_list.create_collector_button" />
                        </Button>
                    </Box>
                </Box>
            )}

            {/* main content */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: "center", py: 8, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: "error.main", mb: 2 }}>
                        {error}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={getCollector}
                        sx={{ borderRadius: "20px", textTransform: "none" }}
                    >
                        <FormattedMessage id="collect_list.retry" />
                    </Button>
                </Box>
            ) : collectors.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: "text.secondary", mb: 2 }}>
                        {hasCollector ? (
                            <FormattedMessage id="collect_list.one_collector_limit_reached" />
                        ) : (
                            <FormattedMessage id="collect_list.no_collectors" />
                        )}
                    </Typography>
                    {!hasCollector && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setCreateDialog({ open: true })}
                            sx={{ borderRadius: "20px", textTransform: "none" }}
                        >
                            <FormattedMessage id="collect_list.create_collector_button" />
                        </Button>
                    )}
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                    <Grid container spacing={3}>
                        {collectors.map((collector) => {
                            let overallStatus = collector.status || "draft";
                            if (overallStatus === "paused") {
                                overallStatus = "draft";
                            }
                            const statusStyle = getCollectorStatusStyling(overallStatus);
                            return (
                                <Grid item xs={12} md={6} lg={4} key={collector.id}>
                                    <CollectorCard
                                        collector={collector}
                                        surveyId={surveyId}
                                        surveyStatus={surveyStatus}
                                        statusStyle={statusStyle}
                                        overallStatus={overallStatus}
                                        onLockClick={openConfirmCollectorLock}
                                        onDeleteClick={(c) => setDialog({ open: true, id: c.id, name: c.name })}
                                        onEditClick={(c) => navigate(`${routes.collector}/${surveyId}/${c.id}/configure`)}
                                        onCopyLink={handleCopyLink}
                                        onViewFailedEmails={(id) => {
                                            setShowFailedEmails(true);
                                            fetchFailedEmails(id);
                                        }}
                                        onDuplicateClick={handleDuplicateCollector}
                                        onRenameCollector={handleRenameCollector}
                                        onReminderClick={handleSendReminder}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Failed Emails Dialog - Outside the Grid */}
                </Box>
            )}

            {/* delete collector dialog */}
            <SimpleDialog
                open={dialog.open}
                title={<FormattedMessage id="collector.delete_collector" />}
                content={
                    <FormattedMessage
                        id="collector.confirm_delete"
                        values={{ name: dialog.name }}
                    />
                }
                onConfirm={handleDelete}
                setOpen={(open) => setDialog({ ...dialog, open })}
            />

            {/* lock collector dialog */}
            <SimpleDialog
                open={lockDialog.open}
                title={lockDialog.title}
                content={lockDialog.content}
                onConfirm={() => {
                    handleToggleLock(lockDialog.id);
                    setLockDialog({ ...lockDialog, open: false });
                }}
                setOpen={(open) => setLockDialog({ ...lockDialog, open })}
            />

            {/* create collector dialog */}
            <Dialog open={createDialog.open} onClose={() => setCreateDialog({ open: false })}>
                <DialogTitle sx={{ fontWeight: "bold", color: colors.main }}>
                    <FormattedMessage id="collect_list.create_dialog_title" />
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                        <FormattedMessage id="collect_list.create_dialog_description" />
                    </Typography>
                    <TextField
                        label={<FormattedMessage id="collect_list.create_dialog_name_label" />}
                        value={newCollector.name}
                        onChange={(e) => setNewCollector({ ...newCollector, name: e.target.value })}
                        fullWidth
                        sx={{ mb: 3 }}
                        helperText={<FormattedMessage id="collect_list.create_dialog_name_helper" />}
                    />
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>
                            <FormattedMessage id="collect_list.create_dialog_type_label" />
                        </InputLabel>
                        <Select
                            value={newCollector.type}
                            onChange={(e) => setNewCollector({ ...newCollector, type: e.target.value })}
                            label={<FormattedMessage id="collect_list.create_dialog_type_label" />}
                        >
                            <MenuItem value="EMAIL">
                                <FormattedMessage id="collect_list.create_dialog_type_email" />
                            </MenuItem>
                            <MenuItem value="LINK">
                                <FormattedMessage id="collect_list.create_dialog_type_link" />
                            </MenuItem>
                        </Select>
                        <FormHelperText>
                            <FormattedMessage id="collect_list.create_dialog_type_helper" />
                        </FormHelperText>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setCreateDialog({ open: false })}
                        sx={{ textTransform: "none" }}
                    >
                        <FormattedMessage id="collect_list.create_dialog_cancel" />
                    </Button>
                    <Button
                        onClick={handleCreateCollector}
                        variant="contained"
                        color="primary"
                        disabled={surveyStatus === "closed"}
                        sx={{ textTransform: "none", borderRadius: "20px", px: 3 }}
                    >
                        <FormattedMessage id="collect_list.create_dialog_create" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const Styles = {
    headerBox: {
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(0, 67, 142, 0.1)',
        p: 4,
        py: 2,
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #00438E, #4A87C8)',
            borderRadius: '20px 20px 0 0'
        }
    },
    headerTitle: {
        fontWeight: 700,
        mb: 0.5,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        fontSize: '1.1rem',
        WebkitTextFillColor: 'transparent'
    },
    headerSubtitle: {
        color: 'text.secondary',
        fontSize: '0.9rem',
        fontWeight: 500
    },
    closedSurveyBanner: {
        mb: 3,
        p: 2,
        backgroundColor: "rgba(244, 67, 54, 0.1)",
        border: "2px solid #f44336",
        borderRadius: "12px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
    },
    backButton: {
        color: colors.main,
        backgroundColor: 'rgba(0, 67, 142, 0.08)',
        borderRadius: '12px',
        p: 1.5,
        '&:hover': {
            backgroundColor: 'rgba(0, 67, 142, 0.15)',
            transform: 'translateY(-1px)'
        },
        transition: 'all 0.2s ease-in-out'
    }
}

export default CollectList;
